import * as Sentry from "@sentry/react";
import { toEDNString, toEDNStringFromSimpleObject } from "edn-data";
import { App, AppUser, UserRole } from "./types/app";
import { HomeRecord, homePathQuery, homeWrite } from "./types/home";
import { ConnectorTableResponse } from "./types/pullConnector";
import { PushConnectorData } from "./types/pushConnector";
import { redactApiErrorBody } from "./lib/redact";

const omegaUrl = process.env.REACT_APP_OMEGA_URL;
const omegaEnv = process.env.REACT_APP_OMEGA_ENV;
const omegaDomain = process.env.REACT_APP_OMEGA_DOMAIN;
const omegaWsUrl = process.env.REACT_APP_OMEGA_WS_URL;

// The caller credential's SOURCE (Auth0 getAccessTokenSilently), set by useFetchAuthToken: the API Token
// Source (pages/frontend/terms.html). queryOmega takes its token from the source instead of a copy captured at
// login, so the Auth0 SDK renews an access token past its 24h lifetime instead of the app re-sending a dead one
// (pages/frontend/api-credential.html). Forwarded as x-omega-credential on query_clj (Qo.Cred/credential!).
export type ApiTokenSource = (options?: { cacheMode?: "on" | "off" }) => Promise<string>;

// The token queryOmega last got from the source, reused for SOURCE_TTL_MS. The SDK only hands out a token with more
// than 60 s left (auth0-spa-js 2.8.0 CacheManager, expiry adjustment 60 s), so a token reused for 30 s never expires
// in flight; and every SDK call re-renders each useAuth0() consumer (auth0-react 2.8.0 dispatches
// GET_ACCESS_TOKEN_COMPLETE with a freshly parsed user). A failed fetch is held too (token null), so a renewal that
// keeps failing (consent_required on localhost, blocked third-party cookies) costs one Auth0 /authorize per 30 s.
const SOURCE_TTL_MS = 30_000;
let held: { token: string | null; at: number } | null = null;
// When the last forced renewal started (renewedToken).
let lastForcedAt = 0;

let apiTokenSource: ApiTokenSource | null = null;
export const setApiTokenSource = (source: ApiTokenSource | null) => {
  apiTokenSource = source;
  held = null;
  lastForcedAt = 0;
};

// The engine's 500 body is {"reason": "<typed error as a JSON string>", "stack": …}; Qo.Auth0/guard-status
// (query-omega-oql oql/app/auth0.oql) throws omega/auth0/unauthorized when Auth0 /userinfo refuses the token.
const isAuth0Unauthorized = (resBody: any): boolean => {
  try {
    return JSON.parse(resBody?.reason)?.type === "omega/auth0/unauthorized";
  } catch {
    return false;
  }
};

type QueryParamValue = string | number | boolean;
type QueryParams = Record<string, QueryParamValue>;

const constructUrlParams = (
  baseUrl: string,
  params: QueryParams,
) => {
  const queryString = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");

  return `${baseUrl}?${queryString}`;
};

export const websocketBaseUrl = () => {
  if (omegaWsUrl) return omegaWsUrl.replace(/\/$/, "");
  if (omegaDomain) return `wss://${omegaDomain}`;
  if (!omegaUrl) {
    throw new Error(
      "Missing REACT_APP_OMEGA_URL or REACT_APP_OMEGA_WS_URL for websocket connection",
    );
  }

  const url = new URL(omegaUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString().replace(/\/$/, "");
};

export const newWebSocket = (options: { path: string; queryParams: any }) => {
  const { queryParams, path } = options;
  const wsUrl = constructUrlParams(
    `${websocketBaseUrl()}/${path}`,
    queryParams,
  );
  const ws = new WebSocket(wsUrl);
  return ws;
};

const postQuery = async (query: string, credential: string | null) => {
  const omegaEnvHeader = omegaEnv ? { oql_env_name: omegaEnv } : {};
  const headers = {
    ...omegaEnvHeader,
    "Content-Type": "text/plain",
    ...(credential ? { "x-omega-credential": credential } : {}),
  };
  const fullUrl = constructUrlParams(
    `${omegaUrl}/query_clj`,
    omegaEnvHeader as any,
  );
  return fetch(fullUrl, {
    method: "POST",
    headers: headers as any,
    body: query,
  })
    .then(async (res) => {
      if (res.status === 500) {
        throw await res.json();
      }
      return res;
    })
    .then((res) => res.json())
    .then((jsonResponse) => jsonResponse["result"]);
};

// Sentry gets an engine 500 only once queryOmega gives up on it, so a refused-then-renewed query is not reported
// (this moved out of postQuery; network errors, thrown as Error, were never reported and still are not).
// The engine echoes the failing OQL in `stack`, and a failed llm-login write carries the pasted token
// there, so the body is scrubbed on the way out (src/lib/redact.ts).
const reportApiError = (resBody: unknown) => {
  if (resBody instanceof Error) return;
  Sentry.captureException(new Error("API 500 Error"), { extra: { responseBody: redactApiErrorBody(resBody) } });
};

// The held token, or a fresh one from the source once the held one is older than SOURCE_TTL_MS. A source failure sends
// the query WITHOUT a credential, as before the source existed: ungated reads still work, gated ones answer
// MISSING_CREDENTIAL_EXCEPTION.
const currentToken = async (source: ApiTokenSource): Promise<string | null> => {
  if (held && Date.now() - held.at < SOURCE_TTL_MS) return held.token;
  try {
    held = { token: await source(), at: Date.now() };
  } catch (e) {
    console.error("FAILED_TO_GET_API_TOKEN", e);
    held = { token: null, at: Date.now() };
  }
  return held.token;
};

export const queryOmega = async (query: string) => {
  const credential = apiTokenSource ? await currentToken(apiTokenSource) : null;
  try {
    return await postQuery(query, credential);
  } catch (resBody) {
    // The engine refused a token the SDK still holds as unexpired (revoked, or expired early):
    // retry this query once with a renewed token.
    const renewed =
      apiTokenSource && isAuth0Unauthorized(resBody) ? await renewedToken(apiTokenSource, credential) : null;
    if (!renewed) {
      const safeBody = redactApiErrorBody(resBody);
      reportApiError(safeBody);
      throw safeBody;
    }
    try {
      return await postQuery(query, renewed);
    } catch (retryBody) {
      const safeBody = redactApiErrorBody(retryBody);
      reportApiError(safeBody);
      throw safeBody;
    }
  }
};

// One forced renewal at a time, and at most one per FORCED_RENEWAL_COOLDOWN_MS. Every new token costs the engine one
// Auth0 /userinfo call (Qo.Auth0/fetch-user!), and /userinfo rate limits are why Qo.Auth0 caches a token's user
// (query-omega-api pages/api/auth0-cache.html). So a burst of refused queries shares ONE renewal, a query whose
// refused token was already replaced takes the replacement, and an engine that keeps refusing fresh tokens (an
// Auth0 outage) gets failures, not a renewal loop.
const FORCED_RENEWAL_COOLDOWN_MS = 60_000;
let renewing: Promise<string | null> | null = null;
const renewedToken = async (source: ApiTokenSource, refused: string | null): Promise<string | null> => {
  if (renewing) return renewing;
  if (held?.token && held.token !== refused) return held.token;
  if (Date.now() - lastForcedAt < FORCED_RENEWAL_COOLDOWN_MS) return null;
  lastForcedAt = Date.now();
  renewing = forceRenewal(source, refused).finally(() => {
    renewing = null;
  });
  return renewing;
};

// auth0-spa-js 2.8.0 dedupes getTokenSilently on clientId::audience::scope, NOT on cacheMode (dev build :2542,
// singlePromise :1864-1875), so a forced call can join a cached call already in flight and come back with the
// refused token. Retry the forced call, at most three times, until the SDK returns a different token.
const forceRenewal = async (source: ApiTokenSource, refused: string | null): Promise<string | null> => {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const token = await source({ cacheMode: "off" });
      if (token !== refused) {
        held = { token, at: Date.now() };
        return token;
      }
    } catch (e) {
      console.error("FAILED_TO_RENEW_API_TOKEN", e);
      return null;
    }
  }
  return null;
};

export const queryOmegaSingleResult = async (query: string) => {
  const response = await queryOmega(query);
  return response["data"][0][0];
};

export const getConnectorTypes = async () => {
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-connector-types {} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response["connector-types"];
};

export interface GetAppsResponse {
  apps: App[];
  email?: string;
  "selected-app"?: string;
}

export const getApps: (config: {
  authToken: string;
}) => Promise<GetAppsResponse> = async ({ authToken }) => {
  const ednString = toEDNString({
    map: [["auth-token", authToken]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-apps ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export interface GetAllWorkspacesResponse {
  apps: App[];
}

export const getAllWorkspaces: (config?: {}) => Promise<GetAllWorkspacesResponse> =
  async (config = {}) => {
    const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-all-workspaces {} Response)
      (return [Response])
    `;
    const response = await queryOmegaSingleResult(query);
    return response;
  };

export interface GetAllUsersResponse {
  users: AppUser[];
}

export const getAllUsers: (config?: {}) => Promise<GetAllUsersResponse> =
  async (config = {}) => {
    const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/all-users {} Response)
      (return [Response])
    `;
    const response = await queryOmegaSingleResult(query);
    return response;
  };

export interface GetWorkpaceUsersResponse {
  users: AppUser[];
}

export const getWorkspaceUsers: (config: {
  appId: string;
}) => Promise<GetWorkpaceUsersResponse> = async ({ appId }) => {
  const ednString = toEDNString({
    map: [["app-id", appId]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/workspace-users ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const addWorkspaceUser: (config: {
  appId: string;
  email: string;
  name: string;
}) => Promise<any> = async ({ appId, email, name }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["email", email],
      ["name", name],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/add-workspace-user ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const setSelectedApp: (config: {
  appId: string;
  email: string;
}) => Promise<any> = async ({ appId, email }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["email", email],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/set-selected-app ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

// The caller's Home Record (system:#home) through the generic read wrapper (Qo.Public.Api/path-query ->
// Qo.Path.Pq/path-query -> Qo.Pq.Home), the listPageProps shape (api/page/pageProp.ts:13-32).
export const getHome: () => Promise<HomeRecord> = async () => {
  const req = toEDNStringFromSimpleObject(homePathQuery, { keysAs: "string" });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/path-query ${req} Response)
      (return [Response])
    `;
  return queryOmegaSingleResult(query);
};

// "Set as home": the caller's own Home Record with "app-id" changed, through the generic write wrapper
// (Qo.Public.Api/write-data -> Qo.Wr/write-data -> the Home Writable), the same write
// `qo write "system:#home" --data '{"app-id":…}'` makes. The Writable self-gates, requires membership
// (omega/query-omega/user-apps/NotAMemberException) and answers the fresh Home Record (query-omega-oql
// oql/app/home.oql:120-125, passed through by oql/app/write.oql:8-14); the selected workspace is untouched.
export const setHome: (config: { appId: string }) => Promise<HomeRecord> = async ({ appId }) =>
  writeData(homeWrite(await getHome(), appId));

export const deleteWorkspaceUser: (config: {
  appId: string;
  email: string;
}) => Promise<any> = async ({ appId, email }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["email", email],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-workspace-user ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const deleteWorkspace: (config: {
  appId: string;
}) => Promise<any> = async ({ appId }) => {
  const ednString = toEDNString({
    map: [["app-id", appId]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-app ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const renameWorkspace: (config: {
  appId: string;
  name: string;
}) => Promise<any> = async ({ appId, name }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["name", name],
    ],
  });
  const query = `
    (datastore Qo.Public.Api "omega/query-omega/public/api")
    (Qo.Public.Api/rename-app ${ednString} Response)
    (return [Response])
  `;
  return queryOmegaSingleResult(query);
};

export const addWorkspace: (config: {
  ownerEmail: string;
  ownerName: string;
  appName: string;
}) => Promise<any> = async ({ ownerName, appName, ownerEmail }) => {
  const ednString = toEDNString({
    map: [
      ["owner-email", ownerEmail],
      ["owner-name", ownerName],
      ["app-name", appName],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/add-app ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getConnectors: (config: {
  appId: string;
}) => Promise<any> = async ({ appId }) => {
  const ednString = toEDNString({
    map: [["app-id", appId]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-connectors ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const deletePullConnector: (config: {
  appId: string;
  connectorId: string;
}) => Promise<any> = async ({ appId, connectorId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["connector-id", connectorId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPushConnectors: (config: {
  appId: string;
}) => Promise<any> = async ({ appId }) => {
  const ednString = toEDNString({
    map: [["app-id", appId]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-push-connectors ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getPushConnector: (config: {
  appId: string;
  pushConId: string;
}) => Promise<any> = async ({ appId, pushConId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["push-con-id", pushConId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api.PushCon "omega/query-omega/public/api/push-connector")
      (Qo.Public.Api.PushCon/get-push-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export interface GetRolesResponse {
  email: string;
  roles: UserRole[];
}

export const getRoles: (config: {
  email: string;
}) => Promise<GetRolesResponse> = async ({ email }) => {
  const ednString = toEDNString({
    map: [["email", email]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-roles ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const getConnectorTables: (config: {
  appId: string;
  connectorId: string;
}) => Promise<ConnectorTableResponse> = async ({ appId, connectorId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["connector-id", connectorId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/get-connector-tables ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const deleteConnector: (config: {
  schema: string;
}) => Promise<any> = async ({ schema }) => {
  const ednString = toEDNString({
    map: [["schema", schema]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const createPushConnector: (config: {
  appId: string;
  pushConnector: PushConnectorData;
}) => Promise<any> = async ({ appId, pushConnector }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["push-connector", { map: Object.entries(pushConnector) }],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/create-push-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const editPushConnector: (config: {
  appId: string;
  pushConnectorId: string;
  pushConnector: PushConnectorData;
}) => Promise<any> = async ({ appId, pushConnectorId, pushConnector }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["push-connector-id", pushConnectorId],
      ["push-connector", { map: Object.entries(pushConnector) }],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/edit-push-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
};

export const deletePushConnector: (config: {
  appId: string;
  pushConnectorId: string;
}) => Promise<any> = async ({ appId, pushConnectorId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["push-connector-id", pushConnectorId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-push-connector ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const deletePageBlock: (
  pageId: string,
  blockIndex: number,
) => Promise<any> = async (pageId, blockIndex) => {
  const ednString = toEDNString({
    map: [
      ["note-id", pageId],
      ["block-index", blockIndex],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/delete-note-block ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response["new-note"];
};

export const createConnectCard: (config: {
  schema: string;
  appId: string;
}) => Promise<any> = async ({ schema, appId }) => {
  const ednString = toEDNString({
    map: [
      ["app-id", appId],
      ["schema", schema],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/create-connect-card ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const createConversation: (config: {
  appId: string;
}) => Promise<any> = async ({ appId }) => {
  const ednString = toEDNString({
    map: [["app-id", appId]],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/create-conversation ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const replyConversation: (config: {
  content: string;
  chatId: string;
  pageId: string;
}) => Promise<any> = async ({ content, chatId, pageId }) => {
  const ednString = toEDNString({
    map: [
      ["content", content],
      ["chat-id", chatId],
      ["page-id", pageId],
    ],
  });
  const query = `
      (datastore Qo.Public.Api "omega/query-omega/public/api")
      (Qo.Public.Api/reply-conversation ${ednString} Response)
      (return [Response])
    `;
  const response = await queryOmegaSingleResult(query);
  return response;
  // return new Promise((resolve, reject) => {
  //   setTimeout(() => {
  //     resolve({
  //       "chat-id": "be93e9d9-a2b4-4716-9228-f76fd182010b",
  //       reply:
  //         'I have created a script that processes the "transformed_go_high_level_data" table to extract full names. Here\'s what the script does:\n\n1. **Extracts the Input Table**: It takes the "transformed_go_high_level_data" table, which contains columns like email, first name, and last name.\n2. **Creates a Full Name Column**: It combines the first name and last name columns to create a new column called "full_name".\n3. **Selects Required Columns**: It then selects only the email and full_name columns to create a new table.\n4. **Stores the Result**: The new table, named "full_names_output", contains the email and full names of the contacts.\n\nThe result is saved in a variable called `result`, which contains the new table with the specified columns.',
  //     });
  //   }, 2000);
  // });
};

export const deleteData: (deletable: any) => Promise<any> = async (
  deletable,
) => {
  const ednString = toEDNStringFromSimpleObject(deletable, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api "omega/query-omega/public/api")
        (Qo.Public.Api/delete-data ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};

export const writeData: (writable: any) => Promise<any> = async (writable) => {
  const ednString = toEDNStringFromSimpleObject(writable, {
    keysAs: "string",
  });
  const query = `
        (datastore Qo.Public.Api "omega/query-omega/public/api")
        (Qo.Public.Api/write-data ${ednString} Response)
        (return [Response])
      `;
  const response = await queryOmegaSingleResult(query);
  return response;
};
